import React, {
  type SetStateAction,
  useState,
  useEffect,
  useCallback,
} from "react";
import ViolationToggleButtons from "./ViolationToggleButtons";
import ViolationsFloatingMenu from "./ViolationsFloatingMenu";
import ViolationFloatingButton from "@/pages/roster-create/components/ViolationFloating";
import type * as type from "@vTypes";
import type {
  ValidateListType,
  ValidationPhase,
} from "@hooks/useRosterValidation.ts";
import { cn } from "@utils/cn.ts";
import TfootSummary from "@components/feature/rosterTable/row_TfootSummary.tsx";
import RosterGrid from "@components/feature/roster_create/components/roster-grid.tsx";
import { shiftGbName } from "@/utils/shiftgb-name";
import type { CopyNurses } from "@/pages/roster-create/Roster_create";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { type ShiftLookupMaps } from "@/utils/shift-util";
import { useRosterCreateUiStore } from "@/store/rosterCreateUiStore";
import useStore from "@/store/store";
import { useShallow } from "zustand/react/shallow";
import { useRosterSummaryStats } from "@/hooks/roster/useRosterSummaryStats";
import { useRosterViolationFilter } from "@/hooks/roster/useRosterViolationFilter";
import ShiftCode from "@/components/shift/ShiftCode";
import { RosterGridType } from "../roster_create/components/roster-grid-type";

/** 위반사항 표시 토글 상태 — 외부에서 관리해 주입 */
export type ViolationToggleState = {
  showManpowerViolations: boolean;
  showConfigViolations: boolean;
  showWantedViolations: boolean;
  toggleManpowerViolations: () => void;
  toggleConfigViolations: () => void;
  toggleWantedViolations: () => void;
};

function RosterMadeTable({
  defaultShiftCode,
  year,
  month,
  showManpowerButton,
  manPower_monthly,
  useMaxManpower = false,
  memo,
  setMemo,
  isEditableMemo,
  copyNurses,
  children,
  gridType = "ROSTER_CREATE",
  hideWantedToggle = false,
  /** true면 위반/원티드 토글 행을 렌더하지 않음 (상위 RosterViewSettings 등에서 배치) */
  hideViolationToggleRow = false,
  /** true면 플로팅 위반 버튼·메뉴는 렌더하지 않음 (`RosterViolationFloatingControls`를 상위에 배치) */
  hideViolationFloating = false,
  /** 최소인원 위반 토글 버튼 숨김 (원티드 조정판 등) */
  hideManpowerToggle = false,
  /** 전월 tail 표시 여부 (레이아웃용) */
  hasPrevTail = false,
  /** 외부 토글 상태 — 미전달 시 보기 설정과 동일한 전역 스토어 사용 */
  violationState,
  onSummaryMinimizedChange,
  onSummaryExpandedChange,
  /** 상위에서 `useShiftLookupMaps(defaultShiftCode, shiftsData)` 등으로 빌드한 맵 — 본문 TableBody와 동일 인스턴스 권장 */
  shiftLookupMaps,
  /** 외부 검증 결과 (useRosterValidation의 validateList) — null/undefined 시 위반사항 없음 */
  rawValidateList,
  isValidating = false,
  phase = "idle",
  /** 현재 사용자 역할 (권한 분기 전용) */
  role = "nurse",
  /** 근무 설정 화면으로 이동 콜백 — 미전달 시 버튼 숨김 */
  onNavigateConfig,
  /** 프리셉티 카운트 안내 배지 표시 여부 */
  showPrecepteeBadge = false,
}: {
  defaultShiftCode: type.availableShifts[];
  year: number;
  month: number;
  manPower_monthly: type.ManPowerMonthly | null;
  useMaxManpower?: boolean;
  showManpowerButton: boolean;
  memo: string;
  setMemo?: React.Dispatch<SetStateAction<string>>;
  isEditableMemo: boolean;
  copyNurses: CopyNurses;
  children?: React.ReactNode;
  gridType?: "ROSTER_CREATE" | "ROSTER_ONLY_VIEW";
  hideWantedToggle?: boolean;
  hideViolationToggleRow?: boolean;
  /** 플로팅 위반 UI를 상위에서 렌더할 때 true */
  hideViolationFloating?: boolean;
  /** 최소인원 위반 토글 버튼 숨김 */
  hideManpowerToggle?: boolean;
  /** 전월 tail 표시 여부 (레이아웃용) */
  hasPrevTail?: boolean;
  /** 미전달 시 전역 violation-visibility 스토어 */
  violationState?: ViolationToggleState;
  onSummaryMinimizedChange?: (isMinimized: boolean) => void;
  onSummaryExpandedChange?: (isExpanded: boolean) => void;
  /** 외부에서 빌드한 시프트 lookup 맵 (필수) */
  shiftLookupMaps: ShiftLookupMaps<type.availableShifts>;
  /** 외부 검증 결과 (useRosterValidation의 validateList) — null/undefined 시 위반사항 없음 */
  rawValidateList?: ValidateListType | null;
  /** 검증 진행 중 여부 */
  isValidating?: boolean;
  /** 검증 단계 */
  phase?: ValidationPhase;
  /** 현재 사용자 역할 (권한 분기 전용) */
  role?: "admin" | "head_nurse" | "nurse";
  /** 근무 설정 화면으로 이동 콜백 — 미전달 시 버튼 숨김 */
  onNavigateConfig?: () => void;
  /** 프리셉티 카운트 안내 배지 표시 여부 (caller가 `getPrecepteePolicy` 결과를 전달) */
  showPrecepteeBadge?: boolean;
}) {
  const isAdmin = role === "admin";
  const isHeadNurse = role === "head_nurse";
  const isViewOnly = gridType === "ROSTER_ONLY_VIEW";

  const setUiMode = useRosterCreateUiStore((s) => s.setMode);
  const toggleManpowerModal = useCallback(() => {
    const m = useRosterCreateUiStore.getState().mode;
    setUiMode(m === "manpower" ? "none" : "manpower");
  }, [setUiMode]);

  const [isExpanded, setIsExpanded] = useState(false);

  /** 1번 아이콘: 통계 그리드 ↔ 일자별 최소인원 요약 바 (초기: 상세 펼침) */
  const [isSummaryMinimized, setIsSummaryMinimized] = useState(false);
  useEffect(() => {
    onSummaryMinimizedChange?.(isSummaryMinimized);
  }, [isSummaryMinimized, onSummaryMinimizedChange]);
  useEffect(() => {
    onSummaryExpandedChange?.(isExpanded);
  }, [isExpanded, onSummaryExpandedChange]);

  const [isFloatingMenuOpen, setIsFloatingMenuOpen] = useState(false);

  const handleToggleFloatingMenu = useCallback(() => {
    setIsFloatingMenuOpen((prev) => !prev);
  }, []);

  const handleCloseFloatingMenu = useCallback(() => {
    setIsFloatingMenuOpen(false);
  }, []);

  // 위반사항 표시 — prop이 없으면 보기 설정과 동일한 전역 스토어(violation-visibility-slice)
  const noOpToggle = useCallback(() => {}, []);
  const violationStateFromStore = useStore(
    useShallow((s) => ({
      showManpowerViolations: s.showManpowerViolations,
      showConfigViolations: s.showConfigViolations,
      showWantedViolations: s.showWantedViolations,
      toggleManpowerViolations: s.toggleManpowerViolations,
      toggleConfigViolations: s.toggleConfigViolations,
      toggleWantedViolations: s.toggleWantedViolations,
    }))
  );
  const {
    showManpowerViolations = false,
    showConfigViolations = false,
    showWantedViolations = false,
    toggleManpowerViolations = noOpToggle,
    toggleConfigViolations = noOpToggle,
    toggleWantedViolations = noOpToggle,
  } = violationState ?? violationStateFromStore;

  // --- 위반 필터 훅 ---
  const { validateList, filteredViolationCount, hasManpowerViolationByShift } =
    useRosterViolationFilter({
      rawValidateList,
      showManpowerViolations,
      showConfigViolations,
    });

  // --- 통계 집계 훅 ---
  const {
    summaryRowGroups,
    totalSummaryRowCount,
    lastSummaryRow,
    orderedMainShiftsForStats,
    mainShifts,
    hasMoreShifts,
  } = useRosterSummaryStats({
    copyNurses,
    defaultShiftCode,
    shiftLookupMaps,
    year,
    month,
    isExpanded,
    isSummaryMinimized,
    gridType,
  });

  /** 하단 요약 표: 동일 shift_id 다중 행일 때 id로 시프트 정의 조회 */
  const resolveSummaryShiftInfo = useCallback(
    (item: { shiftId: string; shiftDbId?: number }) => {
      if (item.shiftDbId != null && Number.isFinite(Number(item.shiftDbId))) {
        return defaultShiftCode.find((s) => s.id === item.shiftDbId);
      }
      return defaultShiftCode.find((s) => s.shift_id === item.shiftId);
    },
    [defaultShiftCode]
  );

  // mainShifts는 통계용이지만 hasMoreShifts 계산은 훅에서 이미 함
  void mainShifts;

  return (
    <>
      <RosterGrid gridType={RosterGridType.HEADER} hasPrevTail={hasPrevTail}>
        {/* MEMO: 접힘 시 말줄임 미리보기 → 클릭 시 통계 상세·메모 전체 펼침(외부 클릭으로 다시 접지 않음) */}

        <div className={cn("flex min-w-0 gap-5")}>
          {gridType === "ROSTER_ONLY_VIEW" && !isHeadNurse && !isAdmin ? (
            <div
              className={cn(
                "border flex-1 rounded-lg overflow-hidden",
                isSummaryMinimized
                  ? "min-h-10 flex-row items-stretch overflow-hidden bg-[center_-30px] bg-cover bg-no-repeat "
                  : "bg-cover bg-center bg-no-repeat"
              )}
              style={{
                backgroundImage: `url(/asset/view/tongtong_view.png)`,
              }}
            ></div>
          ) : (
            <>
              {children ? (
                children
              ) : (
                <div
                  className={cn(
                    "flex flex-1 rounded-lg  overflow-hidden border border-[#D6D6DE] bg-white",
                    !isEditableMemo && "opacity-70",
                    isSummaryMinimized
                      ? "min-h-10 flex-row items-stretch overflow-hidden"
                      : "flex-col items-start justify-start"
                  )}
                >
                  {isSummaryMinimized ? (
                    <>
                      <div className=" flex shrink-0 items-center border-r border-[#D6D6DE] bg-[#F0F0F2] px-3 py-2 text-xs">
                        <h3 className="font-semibold leading-none text-zinc-800">
                          MEMO
                        </h3>
                      </div>
                      {isEditableMemo ? (
                        <button
                          type="button"
                          className="min-w-0 flex-1 px-3 py-2 text-left text-sm text-zinc-700 outline-none transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-300"
                          onClick={() => setIsSummaryMinimized(false)}
                          title={memo.trim() ? memo : undefined}
                        >
                          <span className="block truncate text-sm">
                            {memo.trim() ? (
                              memo.replace(/\s+/g, " ")
                            ) : (
                              <span className="text-sm text-zinc-400">
                                메모를 입력해주세요
                              </span>
                            )}
                          </span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="min-w-0 flex-1  truncate px-3 py-2 text-left text-sm text-zinc-600 outline-none transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-300"
                          onClick={() => setIsSummaryMinimized(false)}
                          title={memo.trim() ? memo : undefined}
                        >
                          {memo.trim() ? memo.replace(/\s+/g, " ") : "\u00A0"}
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <div
                        className={cn(
                          "-translate-x-1 -translate-y-1 rounded-br-2xl border border-[#D6D6DE] bg-[#F0F0F2] px-4 pb-1.5 pt-3 text-xs"
                        )}
                      >
                        <h3 className="font-semibold">MEMO</h3>
                      </div>
                      {isEditableMemo ? (
                        <textarea
                          className="w-full resize-none border-0 p-2 text-sm! h-full placeholder:text-zinc-400 placeholder:text-sm focus:outline-none focus:ring-0"
                          rows={2}
                          value={memo}
                          onChange={(e) => setMemo?.(e.target.value)}
                          aria-label="메모"
                          placeholder="메모를 입력해주세요"
                        />
                      ) : (
                        <div className="w-full whitespace-pre-wrap px-2 pb-2 pt-1 text-left text-sm">
                          {memo || null}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className="relative flex flex-1 ">
          {showPrecepteeBadge && (
            <div className="pointer-events-none absolute -left-2 -top-2 z-20">
              <TooltipProvider delayDuration={200}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        "pointer-events-auto group flex size-6 shrink-0 items-center justify-center rounded-full border-0 bg-white/90 p-0 shadow-sm ring-1 ring-zinc-200/80",
                        "transition-opacity hover:opacity-90",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
                      )}
                      aria-label="프리셉티 카운트 안내"
                    >
                      <img
                        src="/asset/icon/config/icon_2.svg"
                        alt=""
                        aria-hidden
                        className="size-5 object-contain transition-[filter] duration-200 ease-out brightness-100 group-hover:brightness-[0.55]"
                        draggable={false}
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[280px]">
                    <p className="flex flex-wrap items-center gap-0.5 text-sm font-normal">
                      <span className="text-xs">
                        프리셉티의 근무가 카운팅되지 않도록
                      </span>
                      <button
                        type="button"
                        className="text-xs! font-semibold text-zinc-50 underline"
                        onClick={() => onNavigateConfig?.()}
                      >
                        설정
                      </button>
                      <span className="text-xs">되어있습니다.</span>
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}
          {isSummaryMinimized ? (
            <div className="flex w-full flex-1 items-stretch rounded-lg overflow-hidden border border-slate-200 bg-white">
              <div className="flex min-w-0 flex-1 items-center gap-3 pl-4 pr-2 py-2.5">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-2">
                  <span className="shrink-0 text-[13px] font-semibold  text-zinc-800">
                    일자별 {useMaxManpower ? "최대/최소" : "최소"} 근무 인원
                  </span>
                  <div className="flex flex-wrap items-center gap-4">
                    {orderedMainShiftsForStats
                      .filter((s) => s.shift_id)
                      .filter((s) => s.default_shift !== "O")
                      .map((shift) => {
                        const ds = shift.default_shift;
                        const manpowerValue =
                          ds && manPower_monthly != null
                            ? (
                                manPower_monthly as Record<
                                  string,
                                  number | null | undefined
                                >
                              )[ds]
                            : undefined;
                        const manpowerMaxValue =
                          ds && manPower_monthly != null
                            ? (
                                manPower_monthly as Record<
                                  string,
                                  number | null | undefined
                                >
                              )[`${ds}_max`]
                            : undefined;
                        const displayMaxValue =
                          !useMaxManpower || manpowerMaxValue == null
                            ? manpowerValue
                            : Number(manpowerMaxValue) === 0
                              ? "-"
                              : manpowerMaxValue;
                        if (
                          manpowerValue === undefined ||
                          manpowerValue === null
                        ) {
                          return null;
                        }

                        return (
                          <div
                            key={shift.shift_id}
                            className="flex items-center gap-2"
                          >
                            <ShiftCode
                              color={shift.color || ""}
                              code={shift.shift_id}
                            >
                              {hasManpowerViolationByShift(ds) && (
                                <span className="absolute -right-1 -top-1 z-10 size-2 rounded-full bg-destructive ring-1 ring-background" />
                              )}
                            </ShiftCode>

                            <span className="text-sm text-zinc-800">
                              {useMaxManpower && (
                                <>
                                  <span className="font-semibold">
                                    {displayMaxValue}
                                  </span>
                                  <span className="text-xs font-normal text-zinc-600">
                                    /
                                  </span>
                                </>
                              )}
                              <span className="font-semibold">
                                {manpowerValue}
                              </span>
                              <span className="text-xs font-normal text-zinc-600">
                                명
                              </span>
                            </span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
              {/* 요약 상태: 아이콘 가로 배열 — 요약(상세) → 인력설정 → 통계 행 펼침 */}
              <div className="flex shrink-0 flex-row items-center gap-1 bg-white px-2 py-2.5 sm:gap-2 sm:px-3">
                <TooltipProvider delayDuration={200}>
                  <div className="flex flex-row items-center gap-1 sm:gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className={cn(
                            "group flex size-7 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0",
                            "transition-opacity duration-200 hover:opacity-90",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
                          )}
                          onClick={() => setIsSummaryMinimized(false)}
                          aria-label="통계 상세 펼치기"
                        >
                          <img
                            src="/asset/icon/config/icon_1.svg"
                            alt=""
                            aria-hidden
                            className="size-7 object-contain transition-[filter] duration-200 ease-out brightness-100 group-hover:brightness-[0.55]"
                            draggable={false}
                          />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p>통계 상세 보기</p>
                      </TooltipContent>
                    </Tooltip>

                    {showManpowerButton && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className={cn(
                              "group flex size-7 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0",
                              "transition-opacity hover:opacity-90",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
                            )}
                            onClick={toggleManpowerModal}
                            aria-label="근무 인력 설정"
                          >
                            <img
                              src="/asset/icon/config/icon_3.svg"
                              alt=""
                              aria-hidden
                              className="size-7 object-contain transition-[filter] duration-200 ease-out brightness-100 group-hover:brightness-[0.55]"
                              draggable={false}
                            />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          <p>근무 인력 설정</p>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {hasMoreShifts && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className={cn(
                              "group flex size-7 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0",
                              "transition-opacity duration-200 hover:opacity-90",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
                            )}
                            onClick={() => {
                              setIsSummaryMinimized(false);
                              setIsExpanded(true);
                            }}
                            title="근무코드 전체 통계 보기"
                            aria-label="근무코드 전체 통계 보기"
                          >
                            <img
                              src="/asset/icon/config/icon_4.svg"
                              alt=""
                              aria-hidden
                              className={cn(
                                "size-7 object-contain transition-[transform,filter] duration-200 ease-out brightness-100 group-hover:brightness-[0.55]",
                                isExpanded && "rotate-180"
                              )}
                              draggable={false}
                            />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[220px]">
                          <p>
                            {isExpanded
                              ? "추가 근무코드 행을 접습니다."
                              : "근무코드 통계를 펼칩니다."}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TooltipProvider>
              </div>
            </div>
          ) : (
            <div className="flex items-stretch rounded-lg border border-slate-200 bg-white">
              <div className="relative flex flex-col">
                {summaryRowGroups.map((group, groupIndex) => {
                  const isLastGroup =
                    groupIndex === summaryRowGroups.length - 1;
                  const isGroupLastItem =
                    group.items[group.items.length - 1] === lastSummaryRow;

                  return (
                    <div
                      key={group.group_shift_gb}
                      className={cn(
                        "grid grid-cols-[40px_40px_auto] border-b border-dotted border-zinc-300",
                        isLastGroup && isGroupLastItem && "border-b-0"
                      )}
                    >
                      {group.items[0] && (
                        <div
                          className="flex items-center justify-center pr-1  pl-2"
                          style={
                            group.groupLength > 1
                              ? { gridRow: `span ${group.groupLength}` }
                              : undefined
                          }
                        >
                          <div
                            className={cn(
                              " w-full  flex items-center text-sm justify-center text-zinc-500!",
                              !isAdmin && !isHeadNurse && "invisible"
                            )}
                          >
                            {(() => {
                              const firstItem = group.items[0];
                              const shiftInfo = firstItem
                                ? resolveSummaryShiftInfo(firstItem)
                                : undefined;
                              const isNonMainGroup =
                                group.items[0].groupDefaultShift == null;
                              const displayName = isNonMainGroup
                                ? "기타"
                                : shiftInfo?.name ||
                                  shiftGbName(group.group_shift_gb);

                              const defaultShift = shiftInfo?.default_shift;
                              const manpowerValue = defaultShift
                                ? manPower_monthly != null
                                  ? (
                                      manPower_monthly as Record<
                                        string,
                                        number | null | undefined
                                      >
                                    )[defaultShift]
                                  : undefined
                                : undefined;
                              const manpowerMaxValue = defaultShift
                                ? manPower_monthly != null
                                  ? (
                                      manPower_monthly as Record<
                                        string,
                                        number | null | undefined
                                      >
                                    )[`${defaultShift}_max`]
                                  : undefined
                                : undefined;
                              const displayMaxValue =
                                !useMaxManpower || manpowerMaxValue == null
                                  ? manpowerValue
                                  : Number(manpowerMaxValue) === 0
                                    ? "-"
                                    : manpowerMaxValue;
                              const isOffShift = defaultShift === "O";

                              if (isOffShift) {
                                return (
                                  <span className="text-xs text-center">
                                    OFF
                                  </span>
                                );
                              }

                              return manpowerValue !== undefined &&
                                manpowerValue !== null ? (
                                <>
                                  <div
                                    className="text-zinc-700! flex flex-col text-center"
                                    style={{
                                      lineHeight: "0.4",
                                    }}
                                  >
                                    {isExpanded && (
                                      <span className="text-xs ">근무</span>
                                    )}
                                    <div className="flex">
                                      <div
                                        className={cn(
                                          "flex items-center justify-center ",
                                          useMaxManpower && "mr-[1px] mt-[2px]"
                                        )}
                                      >
                                        {useMaxManpower && (
                                          <>
                                            <span className="text-xs font-semibold">
                                              {displayMaxValue}
                                            </span>
                                            <span className="text-xs opacity-70">
                                              /
                                            </span>
                                          </>
                                        )}
                                        <span
                                          className={cn(
                                            "font-semibold text-xs",
                                            !useMaxManpower && "text-sm"
                                          )}
                                        >
                                          {manpowerValue}
                                        </span>
                                      </div>
                                      <span className="text-xs mt-[2px]">
                                        명
                                      </span>
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <span className="text-xs text-center">
                                  {displayName}
                                </span>
                              );
                            })()}
                          </div>
                        </div>
                      )}

                      {group.items.map((item, itemIndex) => {
                        const actualIndex = group.startIndex + itemIndex;
                        const trClass =
                          actualIndex === 0
                            ? "first"
                            : actualIndex + 1 === totalSummaryRowCount
                              ? "last"
                              : "";
                        const currentShiftId = item.shiftId;
                        const shiftInfo = resolveSummaryShiftInfo(item);
                        const daysInMonth = new Date(year, month, 0).getDate();
                        const itemValue =
                          item.data || Array(daysInMonth).fill(0);
                        return (
                          <React.Fragment
                            key={`${item.shiftDbId ?? item.shiftId}-${itemIndex}`}
                          >
                            <div className="flex justify-center items-center px-2">
                              <ShiftCode
                                color={shiftInfo?.color || ""}
                                code={item.shiftId}
                              />
                            </div>

                            <div
                              className={cn(
                                "relative flex-1 border-l border-dotted border-b",
                                itemIndex === group.items.length - 1
                                  ? "border-b-0"
                                  : "border-dotted"
                              )}
                            >
                              <TfootSummary
                                currentShiftId={currentShiftId || ""}
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                shift={shiftInfo || ({} as any)}
                                trClass={trClass}
                                itemValue={itemValue}
                                violations={validateList?.detailed_violations}
                                groupLength={item.groupLength ?? 1}
                                groupIndex={item.groupIndex ?? 0}
                                groupDefaultShift={item.groupDefaultShift}
                              />
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
              {/* 통계 우측 툴바: 1→icon_1(미구현), 2→icon_2, 3→icon_3, 4→icon_4(펼침·absolute) */}
              <div
                className={cn(
                  "relative flex w-[48px] shrink-0 flex-col items-center rounded-tr-lg rounded-r-lg border-l border-dotted border-slate-300 bg-white py-2"
                )}
              >
                <TooltipProvider delayDuration={200}>
                  <div className="flex flex-col items-center gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className={cn(
                            "group flex size-7 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0",
                            "transition-opacity hover:opacity-90",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
                          )}
                          onClick={() => {
                            setIsSummaryMinimized(true);
                            setIsExpanded(false);
                          }}
                          aria-label="일자별 최소 근무 인원 요약으로 접기"
                        >
                          <img
                            src="/asset/icon/config/icon_1.svg"
                            alt=""
                            aria-hidden
                            className="size-7 object-contain transition-[filter] duration-200 ease-out brightness-100 group-hover:brightness-[0.55]"
                            draggable={false}
                          />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p>요약으로 접기</p>
                      </TooltipContent>
                    </Tooltip>

                    {showManpowerButton && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className={cn(
                              "group flex size-7 shrink-0 items-center justify-center rounded-full border-0 bg-transparent p-0",
                              "transition-opacity hover:opacity-90",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
                            )}
                            onClick={toggleManpowerModal}
                            aria-label="근무 인력 설정"
                          >
                            <img
                              src="/asset/icon/config/icon_3.svg"
                              alt=""
                              aria-hidden
                              className="size-7 object-contain transition-[filter] duration-200 ease-out brightness-100 group-hover:brightness-[0.55]"
                              draggable={false}
                            />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="left">
                          <p>근무 인력 설정</p>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {/* 4번: 통계 펼침/접힘 (추가 근무코드 행) */}
                    {hasMoreShifts && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className={cn(
                              "group absolute -bottom-[13px] left-1/2 top-auto z-10 flex size-7 shrink-0 -translate-x-1/2 items-center justify-center rounded-full border-0 bg-white p-0 shadow-sm",
                              "transition-opacity duration-200 hover:opacity-90",
                              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-1"
                            )}
                            onClick={() => setIsExpanded((prev) => !prev)}
                            title={
                              isExpanded
                                ? "통계 접기"
                                : "근무코드 전체 통계 보기"
                            }
                            aria-label={
                              isExpanded
                                ? "통계 접기"
                                : "근무코드 전체 통계 보기"
                            }
                          >
                            <img
                              src="/asset/icon/config/icon_4.svg"
                              alt=""
                              aria-hidden
                              className={cn(
                                "size-7 object-contain transition-[transform,filter] duration-200 ease-out brightness-100 group-hover:brightness-[0.55]",
                                isExpanded && "rotate-180"
                              )}
                              draggable={false}
                            />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-[220px]">
                          <p>
                            {isExpanded
                              ? "추가 근무코드 행을 접습니다."
                              : "근무코드 통계를 펼칩니다."}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TooltipProvider>
              </div>
            </div>
          )}
        </div>
        {/* 플로팅 위반사항 버튼 — 상위 `RosterViolationFloatingControls`로 옮길 때 hideViolationFloating */}
        {!isViewOnly && !hideViolationFloating && (
          <>
            <ViolationFloatingButton
              isValidating={isValidating}
              violationCount={filteredViolationCount}
              phase={phase}
              onClick={handleToggleFloatingMenu}
            />

            <ViolationsFloatingMenu
              validateList={validateList}
              isValidating={isValidating}
              phase={phase}
              hideManpowerToggle={hideManpowerToggle}
              isOpen={isFloatingMenuOpen}
              onClose={handleCloseFloatingMenu}
            />
          </>
        )}
      </RosterGrid>
      {/*위반사항 버튼들 — 상위 보기 설정으로 옮긴 경우 숨김 */}
      {!isViewOnly && !hideViolationToggleRow && (
        <div className="relative  w-full ">
          <ViolationToggleButtons
            hideManpowerToggle={hideManpowerToggle}
            hideWantedToggle={hideWantedToggle}
            showManpowerViolations={showManpowerViolations}
            showConfigViolations={showConfigViolations}
            showWantedViolations={showWantedViolations}
            toggleManpowerViolations={toggleManpowerViolations}
            toggleConfigViolations={toggleConfigViolations}
            toggleWantedViolations={toggleWantedViolations}
          />
        </div>
      )}
    </>
  );
}

export default RosterMadeTable;
